import React, { useCallback, useEffect, useState } from "react";
import { FeatherEdit2, FeatherPlus } from "@subframe/core";
import AddBankAccountModal from "../../../../components/AddBankAccountModal";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { getAllAccounts, type AllAccount } from "../../../../services/api";
import {
  accountLabel,
  accountNumberAside,
  BANK_GROUP_LABEL,
  groupBankAccounts,
  type BankGroup,
} from "./bankAccounts";

/**
 * The account on the left, how it is identified on the right.
 *
 * The right-hand slot is the account number rather than a balance. See
 * `bankAccounts.ts` for why there is no balance here: the column exists, and
 * nothing writes it.
 *
 * Nothing about statement upload on this row, by the user's call: this screen
 * lists accounts and adds one, and where an account number is or is not needed
 * belongs to the screen that needs it. `blocksStatementUpload` is still in
 * `bankAccounts.ts` with the reasoning, unused here.
 */
const AccountRow: React.FC<{
  account: AllAccount;
  fresh: boolean;
  onEdit: (account: AllAccount) => void;
}> = ({ account, fresh, onEdit }) => {
  const number = accountNumberAside(account);

  return (
    <li
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-solid px-3 py-2.5 ${
        // The one we just made, so it can be found in a list of fourteen.
        fresh
          ? "border-brand-300 bg-brand-50"
          : "border-neutral-border bg-default-background"
      }`}
    >
      <span className="flex min-w-0 grow shrink-0 basis-0 flex-wrap items-center gap-2">
        <span className="text-body-bold font-body-bold text-default-font break-words">
          {accountLabel(account)}
        </span>
        {account.is_primary_account && <Badge variant="neutral">Primary</Badge>}
        {!account.is_active && <Badge variant="neutral">Inactive</Badge>}
        {fresh && <Badge variant="brand">Just added</Badge>}
      </span>

      {number !== null && (
        <span className="shrink-0 text-monospace-body font-monospace-body text-subtext-color">
          {number}
        </span>
      )}

      <IconButton
        size="small"
        icon={<FeatherEdit2 />}
        onClick={() => onEdit(account)}
        aria-label={`Edit ${accountLabel(account)}`}
      />
    </li>
  );
};

const GroupSection: React.FC<{
  group: BankGroup;
  accounts: AllAccount[];
  freshId: string | null;
  onEdit: (account: AllAccount) => void;
}> = ({ group, accounts, freshId, onEdit }) => (
  <section className="flex w-full flex-col gap-2">
    <span className="text-body-bold font-body-bold text-subtext-color">
      {BANK_GROUP_LABEL[group]}
    </span>
    <ul className="flex w-full flex-col gap-2">
      {accounts.map((account) => (
        <AccountRow
          key={account.id}
          account={account}
          fresh={account.zoho_bankaccountid === freshId}
          onEdit={onEdit}
        />
      ))}
    </ul>
  </section>
);

/**
 * The company's bank, cash and credit-card accounts.
 *
 * READS THE TENANT, NOT ZOHO. `/api/cashAndBank/all-accounts` returns the local
 * `bank_accounts` rows, which is the honest thing to show: every bank picker,
 * the cash dashboard and the statement pipeline read that table, so an account
 * missing from it is invisible to this app whatever Zoho holds.
 *
 * ADD WRITES TO ZOHO FIRST. `AddBankAccountModal` is the same component Cash &
 * Bank uses, and it posts to `/api/bank-accounts/create`, which creates the
 * account in Zoho Books and only then inserts the local row. So this is a real
 * write into the client's books with no undo — an account added by mistake has
 * to be deleted in Zoho, and one with transactions posted to it cannot be
 * deleted at all. Reusing that modal rather than writing a second form is the
 * point: it already carries the currency list, the opening-balance rules and
 * the review-request flow for users whose permission needs one.
 */
export const BankAccountsList: React.FC = () => {
  const [accounts, setAccounts] = useState<AllAccount[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [freshId, setFreshId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AllAccount | null>(null);

  const load = useCallback(async (): Promise<AllAccount[]> => {
    const result = await getAllAccounts();
    return result.data?.accounts ?? [];
  }, []);

  useEffect(() => {
    let live = true;
    load()
      .then((next) => {
        if (live) {
          setAccounts(next);
          setLoadError(null);
        }
      })
      .catch((err: unknown) => {
        if (live) {
          setLoadError(
            err instanceof Error ? err.message : "Could not load the accounts.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [load]);

  /**
   * After a create or an edit, re-read and mark whatever is new.
   *
   * The modal hands back nothing, so "new" is worked out by diffing the Zoho
   * ids against what was on screen a moment ago. An edit changes no id, so the
   * highlight simply does not fire — which is right, the row is already where
   * the user was looking.
   */
  const refresh = useCallback(() => {
    const before = new Set((accounts ?? []).map((a) => a.zoho_bankaccountid));
    load()
      .then((next) => {
        setAccounts(next);
        const added = next.find((a) => !before.has(a.zoho_bankaccountid));
        setFreshId(added?.zoho_bankaccountid ?? null);
      })
      .catch((err: unknown) => {
        setLoadError(
          err instanceof Error ? err.message : "Could not reload the accounts.",
        );
      });
  }, [accounts, load]);

  const sections = groupBankAccounts(accounts ?? []);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <span className="text-body font-body text-subtext-color">
          {accounts === null
            ? "Reading the accounts..."
            : `${accounts.length} ${accounts.length === 1 ? "account" : "accounts"}`}
        </span>
        <Button
          variant="brand-primary"
          size="small"
          icon={<FeatherPlus />}
          onClick={() => setAddOpen(true)}
        >
          Add account
        </Button>
      </div>

      {loadError !== null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{loadError}</span>
        </div>
      )}

      {accounts !== null && accounts.length === 0 && loadError === null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-amber-200 bg-amber-50 p-3">
          <span className="text-body font-body text-amber-800">
            No accounts here yet. Add one below, or add it in Zoho Books — an
            account that exists only in Zoho stays invisible to this app until
            it has a row here.
          </span>
        </div>
      )}

      {sections.map((section) => (
        <GroupSection
          key={section.group}
          group={section.group}
          accounts={section.accounts}
          freshId={freshId}
          onEdit={setEditing}
        />
      ))}

      <AddBankAccountModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={refresh}
      />

      <AddBankAccountModal
        mode="edit"
        editAccount={editing}
        open={editing !== null}
        onOpenChange={(next: boolean) => {
          if (!next) setEditing(null);
        }}
        onSuccess={refresh}
      />
    </div>
  );
};
